/**
 * Reschedule requests on a portal job (tracker item 45, phase 4): the button and
 * dialog for asking to move a SCHEDULED visit, the lead-time rule, the open
 * request (cancel it), and how earlier requests ended (AUTO_APPROVED / APPROVED /
 * DECLINED with the office's message). A request never edits the visit by
 * itself: when the company auto-approves and nothing is in the way the answer is
 * AUTO_APPROVED and the job page reloads on its new date; otherwise the office
 * decides. Mobile first: the dialog is a bottom sheet on a phone.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PortalJob, PortalRescheduleRequest } from '../../api/types';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import {
  EMPTY_RESCHEDULE_FORM,
  RESCHEDULE_STATUS_LABELS,
  RESCHEDULE_WINDOWS,
  RescheduleForm,
  RequestFailure,
  canAskToReschedule,
  dayAfter,
  describePortalFailure,
  describeRequestFailure,
  localToday,
  looksTooLateToReschedule,
  portalDay,
  rescheduleOutcome,
  rescheduleProblems,
  rescheduleRequestBody,
  rescheduleStatusClass,
} from '../../utils/portal';
import { formatClock } from '../../utils/technician';
import { usePortal, usePortalApi } from './PortalContext';
import { InlineAlert } from './PortalStates';

const SHOWN_HISTORY = 3;

interface Props {
  job: PortalJob;
  /** The visit moved (an automatic approval): reload the job */
  onChanged?: () => void;
}

export function PortalReschedule({ job, onChanged }: Props) {
  const { me, reportFailure } = usePortal();
  const api = usePortalApi();
  const leadHours = me.rescheduleLeadHours;
  const [requests, setRequests] = useState<PortalRescheduleRequest[] | null>(null);
  const [loadProblem, setLoadProblem] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<RescheduleForm>(EMPTY_RESCHEDULE_FORM);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<RequestFailure | null>(null);
  const [notice, setNotice] = useState<{ kind: 'success' | 'info'; text: string } | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState<string | null>(null);
  const openerRef = useRef<HTMLButtonElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const requestId = useRef(0);
  const today = localToday();

  const load = useCallback(async () => {
    const id = ++requestId.current;
    try {
      const rows = await api.listPortalRescheduleRequests(job.id);
      if (id !== requestId.current) return;
      setRequests(rows);
      setLoadProblem(null);
    } catch (error) {
      if (id !== requestId.current) return;
      const described = describePortalFailure(error, 'We could not load your reschedule requests.');
      reportFailure(described);
      setLoadProblem(described.message);
      setRequests((current) => current ?? []);
    }
  }, [api, job.id, reportFailure]);

  useEffect(() => { void load(); }, [load]);

  const close = useCallback(() => {
    setOpen(false);
    setFailure(null);
    setTouched(false);
    openerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    firstFieldRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, close]);

  const openRequest = (requests ?? []).find((r) => r.status === 'OPEN') ?? null;
  const history = (requests ?? []).filter((r) => r.status !== 'OPEN').slice(0, SHOWN_HISTORY);
  const problems = rescheduleProblems(form, today);
  const canAsk = canAskToReschedule(job) && !openRequest;
  const soon = canAskToReschedule(job) && looksTooLateToReschedule(job, leadHours);

  const openDialog = () => {
    setForm(EMPTY_RESCHEDULE_FORM);
    setTouched(false);
    setFailure(null);
    setNotice(null);
    setOpen(true);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (Object.keys(problems).length > 0 || busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const created = await api.createPortalRescheduleRequest(job.id, rescheduleRequestBody(form));
      setOpen(false);
      setTouched(false);
      setNotice(
        created.status === 'AUTO_APPROVED'
          ? { kind: 'success', text: rescheduleOutcome(created, me.companyName) }
          : { kind: 'info', text: `Request sent. ${me.companyName} will confirm the new date and we will email you.` }
      );
      await load();
      if (created.status === 'AUTO_APPROVED') onChanged?.();
    } catch (error) {
      const described = describeRequestFailure(error, { leadHours, company: me.companyName });
      if (described.kind === 'session') reportFailure({ kind: 'session', message: described.message });
      setFailure(described);
      if (described.reload) {
        await load();
        onChanged?.();
      }
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (request: PortalRescheduleRequest) => {
    setBusy(true);
    setFailure(null);
    setNotice(null);
    try {
      await api.cancelPortalRescheduleRequest(job.id, request.id);
      setConfirmingCancel(null);
      setNotice({ kind: 'info', text: 'Your request was cancelled. Your visit stays as it is.' });
      await load();
    } catch (error) {
      const described = describeRequestFailure(error, { leadHours, company: me.companyName });
      if (described.kind === 'session') reportFailure({ kind: 'session', message: described.message });
      setConfirmingCancel(null);
      setFailure(described);
      if (described.reload) await load();
    } finally {
      setBusy(false);
    }
  };

  const set = <K extends keyof RescheduleForm>(field: K, value: RescheduleForm[K]) => setForm((current) => ({ ...current, [field]: value }));
  const error = (field: keyof RescheduleForm) => (touched ? problems[field] : undefined);

  return (
    <section aria-labelledby="reschedule-heading" className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200" data-testid="reschedule-section">
      <h2 id="reschedule-heading" className="mb-2 text-sm font-semibold text-gray-600">Change the date</h2>

      {notice && <div className="mb-3"><InlineAlert kind={notice.kind}>{notice.text}</InlineAlert></div>}
      {!open && failure && <div className="mb-3"><InlineAlert>{failure.message}</InlineAlert></div>}
      {loadProblem && <div className="mb-3"><InlineAlert>{loadProblem}</InlineAlert></div>}

      {openRequest && (
        <div className="rounded-md bg-yellow-50 p-3 ring-1 ring-yellow-200" data-testid="open-reschedule">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm font-semibold text-gray-900">Your request is waiting</p>
            <span className={`rounded px-2 py-0.5 text-xs font-semibold ${rescheduleStatusClass(openRequest.status)}`}>{RESCHEDULE_STATUS_LABELS[openRequest.status]}</span>
          </div>
          <p className="mt-1 text-sm text-gray-800">{rescheduleOutcome(openRequest, me.companyName)}</p>
          {openRequest.reason && <p className="mt-1 break-words text-xs text-gray-600">Your note: {openRequest.reason}</p>}
          {confirmingCancel === openRequest.id ? (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row" role="group" aria-label="Confirm cancelling the request">
              <p className="text-sm text-gray-800 sm:mr-2 sm:self-center">Cancel this request? Your visit stays as it is.</p>
              <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => void cancel(openRequest)}>Yes, cancel request</button>
              <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setConfirmingCancel(null)}>Keep request</button>
            </div>
          ) : (
            <button type="button" className={`${secondaryButtonClass} mt-3`} disabled={busy} onClick={() => { setNotice(null); setConfirmingCancel(openRequest.id); }}>
              Cancel request
            </button>
          )}
        </div>
      )}

      {canAsk && (
        <div>
          <p className="text-sm text-gray-700" data-testid="reschedule-rule">
            Need a different day? Visits can be changed online with at least {leadHours} hours' notice. Closer than that, please call {me.companyName}.
          </p>
          {soon && (
            <p className="mt-2 rounded-md bg-blue-50 p-2 text-xs text-blue-900" data-testid="reschedule-soon">
              This visit is coming up soon. If it is closer than {leadHours} hours away, please call {me.companyName} instead.
            </p>
          )}
          <button ref={openerRef} type="button" className={`${primaryButtonClass} mt-3 w-full sm:w-auto`} onClick={openDialog}>
            Ask to reschedule
          </button>
        </div>
      )}
      {!canAsk && !openRequest && !canAskToReschedule(job) && history.length === 0 && (
        <p className="text-sm text-gray-600">This visit cannot be rescheduled online. Please call {me.companyName} if you need to change it.</p>
      )}

      {history.length > 0 && (
        <ul className="mt-3 space-y-2" aria-label="Earlier reschedule requests">
          {history.map((request) => (
            <li key={request.id} className="rounded-md bg-gray-50 p-3 text-sm" data-testid="reschedule-history">
              <div className="flex items-start justify-between gap-3">
                <span className="text-gray-800">{rescheduleOutcome(request, me.companyName)}</span>
                <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-semibold ${rescheduleStatusClass(request.status)}`}>{RESCHEDULE_STATUS_LABELS[request.status]}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div className="fixed inset-0 z-40 flex items-end justify-center bg-gray-900/50 sm:items-center" data-testid="reschedule-backdrop">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reschedule-dialog-title"
            className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-xl bg-white p-4 shadow-xl sm:rounded-xl"
          >
            <form onSubmit={(e) => void submit(e)} noValidate>
              <h3 id="reschedule-dialog-title" className="text-lg font-bold text-gray-900">Ask to reschedule</h3>
              <p className="mt-1 text-sm text-gray-600">
                Now: {portalDay(job.scheduledDate)}{job.scheduledStartTime ? ` at ${formatClock(job.scheduledStartTime)}` : ''}. This is a request: {me.companyName} confirms the new date.
                Changes need at least {leadHours} hours' notice.
              </p>
              {failure && <div className="mt-3"><InlineAlert>{failure.message}</InlineAlert></div>}

              <div className="mt-4">
                <label htmlFor="rs-date" className={labelClass}>New date</label>
                <input
                  id="rs-date" ref={firstFieldRef} type="date" className={`${inputClass} min-h-[44px]`} min={dayAfter(today)}
                  value={form.date} onChange={(e) => set('date', e.target.value)} disabled={busy}
                  aria-invalid={!!error('date')} aria-describedby={error('date') ? 'rs-date-error' : undefined}
                />
                {error('date') && <p id="rs-date-error" className="mt-1 text-xs text-red-700">{error('date')}</p>}
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="rs-time" className={labelClass}>Start time (optional)</label>
                  <input
                    id="rs-time" type="time" className={`${inputClass} min-h-[44px]`} value={form.startTime}
                    onChange={(e) => set('startTime', e.target.value)} disabled={busy}
                    aria-invalid={!!error('startTime')} aria-describedby={error('startTime') ? 'rs-time-error' : undefined}
                  />
                  {error('startTime') && <p id="rs-time-error" className="mt-1 text-xs text-red-700">{error('startTime')}</p>}
                </div>
                <div>
                  <label htmlFor="rs-window" className={labelClass}>Time of day (optional)</label>
                  <select id="rs-window" className={`${inputClass} min-h-[44px]`} value={form.window} onChange={(e) => set('window', e.target.value)} disabled={busy}>
                    {RESCHEDULE_WINDOWS.map((w) => <option key={w} value={w}>{w || 'No preference'}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <label htmlFor="rs-reason" className={labelClass}>Reason (optional)</label>
                <textarea
                  id="rs-reason" className={inputClass} rows={3} maxLength={500} value={form.reason}
                  onChange={(e) => set('reason', e.target.value)} disabled={busy}
                />
              </div>
              <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button type="button" className={secondaryButtonClass} onClick={close} disabled={busy}>Close</button>
                <button type="submit" className={primaryButtonClass} disabled={busy}>{busy ? 'Sending…' : 'Send request'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
